/// <amd-module name="Release/Client/RestClient/Extended" />
import { IVssRestClient, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

import { ReleaseStatus, ReleaseExpands, ReleaseQueryOrder, Release, DeploymentStatus, DeploymentOperationStatus, Deployment } from "../Contracts/Release";
import { IReleasesResult, IDeploymentsResult } from "../Contracts/Extended";

export interface IExtendedReleaseClient extends IVssRestClient {
    /**
     * Get a list of releases with continuationToken for the next page
     * 
     * @param {string} project - Project ID or project name
     * @param {number} definitionId - Releases from this release definition Id.
     * @param {number} definitionEnvironmentId
     * @param {string} searchText - Releases with names containing searchText.
     * @param {string} createdBy - Releases created by this user.
     * @param {ReleaseStatus} statusFilter - Releases that have this status.
     * @param {number} environmentStatusFilter
     * @param {Date} minCreatedTime - Releases that were created after this time.
     * @param {Date} maxCreatedTime - Releases that were created before this time.
     * @param {ReleaseQueryOrder} queryOrder - Gets the results in the defined order of created date for releases. Default is descending.
     * @param {number} top - Number of releases to get. Default is 50.
     * @param {number} continuationToken - Gets the releases after the continuation token provided.
     * @param {ReleaseExpands} expand - The property that should be expanded in the list of releases.
     * @param {string} artifactTypeId - Releases with given artifactTypeId will be returned. Values can be Build, Jenkins, GitHub, Nuget, Team Build (external), ExternalTFSBuild, Git, TFVC, ExternalTfsXamlBuild.
     * @param {string} sourceId - Unique identifier of the artifact used. e.g. For build it would be {projectGuid}:{BuildDefinitionId}, for Jenkins it would be {JenkinsConnectionId}:{JenkinsDefinitionId}, for TfsOnPrem it would be {TfsOnPremConnectionId}:{ProjectName}:{TfsOnPremDefinitionId}. For third-party artifacts e.g. TeamCity, BitBucket you may refer 'uniqueSourceIdentifier' inside vss-extension.json https://github.com/Microsoft/vsts-rm-extensions/blob/master/Extensions.
     * @param {string} artifactVersionId - Releases with given artifactVersionId will be returned. E.g. in case of Build artifactType, it is buildId.
     * @param {string} sourceBranchFilter - Releases with given sourceBranchFilter will be returned.
     * @param {boolean} isDeleted - Gets the soft deleted releases, if true.
     * @param {string[]} tagFilter - A comma-delimited list of tags. Only releases with these tags will be returned.
     * @param {string[]} propertyFilters - A comma-delimited list of extended properties to be retrieved. If set, the returned Releases will contain values for the specified property Ids (if they exist). If not set, properties will not be included. Note that this will not filter out any Release from results irrespective of whether it has property set or not.
     * @param {number[]} releaseIdFilter - A comma-delimited list of releases Ids. Only releases with these Ids will be returned.
     */
    getReleasesWithToken(project?: string, definitionId?: number, definitionEnvironmentId?: number, searchText?: string, createdBy?: string, statusFilter?: ReleaseStatus, environmentStatusFilter?: number, minCreatedTime?: Date, maxCreatedTime?: Date, queryOrder?: ReleaseQueryOrder, top?: number, continuationToken?: number, folderPath?: string, expand?: ReleaseExpands, artifactTypeId?: string, sourceId?: string, artifactVersionId?: string, sourceBranchFilter?: string, isDeleted?: boolean, tagFilter?: string[], propertyFilters?: string[], releaseIdFilter?: number[]): Promise<IReleasesResult>;

    /**
     * @param {string} project - Project ID or project name
     * @param {number} definitionId
     * @param {number} definitionEnvironmentId
     * @param {string} createdBy
     * @param {Date} minModifiedTime
     * @param {Date} maxModifiedTime
     * @param {Release.DeploymentStatus} deploymentStatus
     * @param {Release.DeploymentOperationStatus} operationStatus
     * @param {boolean} latestAttemptsOnly
     * @param {Release.ReleaseQueryOrder} queryOrder
     * @param {number} top
     * @param {number} continuationToken
     * @param {string} createdFor
     * @param {Date} minStartedTime
     * @param {Date} maxStartedTime
     * @param {string} sourceBranch
     */
    getDeploymentsWithContinuationToken(project: string, definitionId?: number, definitionEnvironmentId?: number, createdBy?: string, minModifiedTime?: Date, maxModifiedTime?: Date, deploymentStatus?: DeploymentStatus, operationStatus?: DeploymentOperationStatus, latestAttemptsOnly?: boolean, queryOrder?: ReleaseQueryOrder, top?: number, continuationToken?: number, createdFor?: string, minStartedTime?: Date, maxStartedTime?: Date, sourceBranch?: string): Promise<IDeploymentsResult>;
}

const continuationTokenHeaderKey = "x-ms-continuationtoken";

class ExtendedReleaseClient extends RestClientBase implements IExtendedReleaseClient {
    public async getReleasesWithToken(project?: string, definitionId?: number, definitionEnvironmentId?: number, searchText?: string, createdBy?: string, statusFilter?: ReleaseStatus, environmentStatusFilter?: number, minCreatedTime?: Date, maxCreatedTime?: Date, queryOrder?: ReleaseQueryOrder, top?: number, continuationToken?: number, folderPath?: string, expand?: ReleaseExpands, artifactTypeId?: string, sourceId?: string, artifactVersionId?: string, sourceBranchFilter?: string, isDeleted?: boolean, tagFilter?: string[], propertyFilters?: string[], releaseIdFilter?: number[]): Promise<IReleasesResult> {
        const queryValues: any = {
            definitionId: definitionId,
            definitionEnvironmentId: definitionEnvironmentId,
            searchText: searchText,
            createdBy: createdBy,
            statusFilter: statusFilter,
            environmentStatusFilter: environmentStatusFilter,
            minCreatedTime: minCreatedTime,
            maxCreatedTime: maxCreatedTime,
            queryOrder: queryOrder,
            '$top': top,
            continuationToken: continuationToken,
            '$expand': expand,
            artifactTypeId: artifactTypeId,
            sourceId: sourceId,
            artifactVersionId: artifactVersionId,
            sourceBranchFilter: sourceBranchFilter,
            isDeleted: isDeleted,
            tagFilter: tagFilter && tagFilter.join(","),
            propertyFilters: propertyFilters && propertyFilters.join(","),
            releaseIdFilter: releaseIdFilter && releaseIdFilter.join(","),
            path: folderPath
        };

        return this.beginRequest<Response>({
            apiVersion: "5.0-preview.8",
            routeTemplate: "{project}/_apis/Release/releases",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            returnRawResponse: true,
        })
            .then((response: Response) => {
                const tokenString = response.headers.get(continuationTokenHeaderKey);
                const nextContinuationToken = tokenString && parseInt(tokenString, 10);
                return response.text()
                    .then((r) => {
                        return {
                            releases: deserializeVssJsonObject<Release[]>(r),
                            continuationToken: nextContinuationToken
                        } as IReleasesResult;
                    });
            });
    }

    public async getDeploymentsWithContinuationToken(
        project: string,
        definitionId?: number,
        definitionEnvironmentId?: number,
        createdBy?: string,
        minModifiedTime?: Date,
        maxModifiedTime?: Date,
        deploymentStatus?: DeploymentStatus,
        operationStatus?: DeploymentOperationStatus,
        latestAttemptsOnly?: boolean,
        queryOrder?: ReleaseQueryOrder,
        top?: number,
        continuationToken?: number,
        createdFor?: string,
        minStartedTime?: Date,
        maxStartedTime?: Date,
        sourceBranch?: string
    ): Promise<IDeploymentsResult> {

        const queryValues: any = {
            definitionId: definitionId,
            definitionEnvironmentId: definitionEnvironmentId,
            createdBy: createdBy,
            minModifiedTime: minModifiedTime,
            maxModifiedTime: maxModifiedTime,
            deploymentStatus: deploymentStatus,
            operationStatus: operationStatus,
            latestAttemptsOnly: latestAttemptsOnly,
            queryOrder: queryOrder,
            '$top': top,
            continuationToken: continuationToken,
            createdFor: createdFor,
            minStartedTime: minStartedTime,
            maxStartedTime: maxStartedTime,
            sourceBranch: sourceBranch
        };

        return this.beginRequest<Response>({
            apiVersion: "5.0-preview.2",
            routeTemplate: "{project}/_apis/Release/deployments",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            returnRawResponse: true
        })
            .then((response: Response) => {
                const tokenString = response.headers.get(continuationTokenHeaderKey);
                const nextContinuationToken = tokenString && parseInt(tokenString, 10);
                return response.text()
                    .then((r) => {
                        return {
                            deployments: deserializeVssJsonObject<Deployment[]>(r),
                            continuationToken: nextContinuationToken
                        } as IDeploymentsResult;
                    });
            });
    }
}

RestClients.add("IExtendedReleaseClient", {
    factory: ExtendedReleaseClient,
    options: {
        serviceInstanceType: "0000000d-0000-8888-8000-000000000000"
    }
});
